// Libs
import React, { useCallback, useEffect, useState } from 'react';
import { Collapse, Icon, Tooltip, Modal } from "antd";
import { useRecoilValue } from 'recoil';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import update from 'immutability-helper';

// Own Libs
import { Button, ColumnProps, Table, Widget } from '@digital/ui';

//Api
import {
    deleteDocumento,
    DesfazerExclusoesHandOverOrdemCompra,
    getDocumentosOrdemCompraHandover,
    incluirDocumento,
    ISasDocumentoOrdemCompra,
    updateListSasDocumentoOrdemCompraCommand,
    updateSasDocumentoOrdemCompraCommand,
    viewArquivo
} from '@app/api/sas/sasDocumentoOrdemCompra';

//Files
import { dadosIdentificacaoState, ordemCompraIdState } from '../atoms';
import { ModalInclusaoMultAnexo, Space, Visibility } from '@app/components';
import { componentsSorting } from '@app/components/SortingTable';

//Utils
import { compareNumber } from '@app/helpers/comparators';
import { GetFileExtension, imageFormats } from '@app/helpers';
import { hasPermission } from '@app/helpers/permissions';
import ModalDeleteAnexo from './ModalDeleteAnexo';
import { EGuidTipoSasDocumento } from '@app/enums/EGuidTipoSasDocumento';
import { baixarTemplateCartaConvite } from '@app/api/template';

const WidgetHandOver = () => {
    const ordemCompraId = useRecoilValue(ordemCompraIdState);
    const [arquivos, setArquivos] = useState<ISasDocumentoOrdemCompra[]>([]);

    const getDocs = () => {
        getDocumentosOrdemCompraHandover({
            ordemCompraId
        }).then(data => {
            if (data) {
                setArquivos(data.ativos);
            }
        });
    };

    useEffect(() => {
        getDocs();
    }, []);

    const onDesfazer = () => {
        DesfazerExclusoesHandOverOrdemCompra(ordemCompraId).then(data => {
            getDocs();
        });
    };

    const onChangeHandover = (documentoId: string, handover: boolean) => {
        updateSasDocumentoOrdemCompraCommand({
            sasDocumentoOrdemCompraId: documentoId,
            handover: handover
        }).then(data => {
            if (data) getDocs();
        });
    };

    const columns = () => {
        let columns: ColumnProps<any>[] = [];

        columns.push({
            title: 'Número Anexo',
            dataIndex: 'ordem',
            width: '5%',
            align: 'center',
            render: (index: number, record: any) => (<span>{record.ordem}</span>)
        });

        columns.push({
            title: 'Descrição',
            dataIndex: 'descricao',
        });

        columns.push({
            title: 'Arquivo',
            dataIndex: 'arquivoNomeDownload',
            width: '30%'
        });

        columns.push({
            title: 'Ações',
            dataIndex: '',
            className: 'td-inicio',
            width: '5%',
            render(text, record, index) {
                return <>
                    <Visibility
                        visible={GetFileExtension(record.arquivoNomeDownload) === "pdf" ||
                            imageFormats.includes(
                                GetFileExtension(record.arquivoNomeDownload)
                            )}
                    >
                        <Tooltip title="Visualizar Arquivo">
                            <Button
                                icon="files-view_file"
                                type="link"
                                onClick={() => {
                                    viewArquivo(
                                        record.arquivoId,
                                        record.arquivoNomeDownload
                                    );
                                }}
                            />
                        </Tooltip>
                    </Visibility>

                    <Visibility
                        visible={record.multiple && !record.fixo}
                        permissions={["SAS_ORDENS_COMPRA_SUBSTITUIR_ANEXO"]}
                    >
                        <ModalDeleteAnexo sasDocumentoOrdemComraId={record.id} />
                    </Visibility>

                    <Visibility
                        visible={true}
                    >
                        <Tooltip title="Excluir Anexos do Handover">
                            <Button
                                onClick={() => {
                                    Modal.confirm({
                                        title: 'Deletar Item',
                                        icon: <Icon type="info-circle" />,
                                        content: 'Deseja excluir todos esses registros do handover?',
                                        onOk() {
                                            onChangeHandover(record.id, false);
                                        },
                                    });
                                }}
                                icon="commons-trash"
                            />
                        </Tooltip>
                    </Visibility>

                    <>
                        {(record.tipoDocumentoOrdemCompraId && [EGuidTipoSasDocumento.CartaConvite].includes(record.tipoDocumentoOrdemCompraId)) ? (
                            <Tooltip title="Baixar Template">
                                <Button
                                    icon="business-new_resume_template"
                                    onClick={() => baixarTemplateCartaConvite()}
                                />
                            </Tooltip>
                        ) : (
                            <></>
                        )}
                    </>
                </>;
            },
        });

        return columns;
    };

    const moveRow = useCallback(
        (dragIndex, hoverIndex) => {
            const dragRow = arquivos[dragIndex];
            const respUpdate = update(arquivos, {
                $splice: [
                    [dragIndex, 1],
                    [hoverIndex, 0, dragRow],
                ],
            });

            const ordem = respUpdate.map((ru, index) => {
                return { ...ru, ordem: index, Descricao: respUpdate[index].descricao };
            });

            updateListSasDocumentoOrdemCompraCommand(ordem)
                .then((data: any) => {
                    if (data && data.valid) {
                        setArquivos(data.sasDocumentoOrdemCompras);
                    }
                });
        },
        [arquivos],
    );

    return (
        <Collapse bordered={false}
            defaultActiveKey='WidgetHandOver'
            activeKey='WidgetHandOver' >
            <Collapse.Panel key='WidgetHandOver' header="Documentação do Handover" showArrow={false} >
                <Widget
                    actions={[
                        <Visibility visible={true}>
                            <Tooltip title="Desfaz todas as exclusões do handover" >
                                <Button title='Desfazer' type='link' onClick={() => onDesfazer()} />
                            </Tooltip>
                        </Visibility>
                    ]}
                >
                    <Visibility
                        visible={hasPermission(["SAS_ORDENS_COMPRA_SUBSTITUIR_ANEXO"])}
                    >
                        <ModalInclusaoMultAnexo
                            buttonIcon="ui-upload"
                            buttonTooltip='Incluir Anexo'
                            buttonLabel='Incluir Anexo'
                            onSubmit={async (values) =>
                                await incluirDocumento({
                                    ordemCompraId: ordemCompraId,
                                    descricao: values.justificativa,
                                    file: values.arquivo,
                                    sasTipoDocumentoId: EGuidTipoSasDocumento.HandOver,
                                }).then((data: any) => {
                                    if (data && data.valid) {
                                        getDocs();
                                        return true;
                                    }
                                    return false;
                                })
                            }
                            showJustificativa
                            isJustificativaInput
                            labelJustificativa="Descrição do Documento"
                            placeholderJustificativa="Descrição do Documento"
                            textButtonSave="Salvar"
                            isMultAnexo={false}
                        />
                        <Space mb={16} />
                    </Visibility>

                    <DndProvider backend={HTML5Backend}>
                        <Table
                            dataSource={arquivos.sort((a, b) => compareNumber(a.ordem, b.ordem))}
                            columns={columns()}
                            rowKey="id"
                            pagination={false}
                            components={componentsSorting}
                            onRow={(record, index) => ({
                                index,
                                moveRow,
                            })}
                        />
                    </DndProvider>
                </Widget>
            </Collapse.Panel>
        </Collapse>
    );
};

export default WidgetHandOver;